import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const KitchenAuthContext = createContext(null);

export const KitchenAuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isSeller, setIsSeller] = useState(false);
  const [sellerId, setSellerId] = useState(null);
  const [sellerUsername, setSellerUsername] = useState('');
  const [shopId, setShopId] = useState(null);

  const clearAuth = useCallback(() => {
    setIsSeller(false);
    setSellerId(null);
    setSellerUsername('');
    setShopId(null);
  }, []);

  const fetchMe = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/kitchen/auth/me', {
        withCredentials: true,
      });

      const data = res.data || {};

      if (data.ok && data.is_seller === true) {
        setIsSeller(true);
        setSellerId(data.seller_id ?? null);
        setSellerUsername(data.seller_username || '');
        setShopId(data.shop_id ?? null);
      } else {
        clearAuth();
      }

      return data;
    } catch (err) {
      clearAuth();
      return {
        ok: false,
        is_seller: false,
      };
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (username, password) => {
    const formData = new URLSearchParams();
    formData.append('username', username || '');
    formData.append('password', password || '');

    const res = await axios.post('/api/kitchen/manage', formData, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    const data = res.data || {};

    if (data.ok) {
      await fetchMe();
    }

    return data;
  }, [fetchMe]);

  const logout = useCallback(async () => {
    clearAuth();
  }, [clearAuth]);

  const value = useMemo(() => ({
    loading,
    isSeller,
    sellerId,
    sellerUsername,
    shopId,
    login,
    logout,
    refreshMe: fetchMe,
    setShopId,
  }), [loading, isSeller, sellerId, sellerUsername, shopId, login, logout, fetchMe]);

  return (
    <KitchenAuthContext.Provider value={value}>
      {children}
    </KitchenAuthContext.Provider>
  );
};

export const useKitchenAuth = () => {
  const context = useContext(KitchenAuthContext);
  if (!context) {
    throw new Error('useKitchenAuth must be used within a KitchenAuthProvider');
  }
  return context;
};